import ApiConfig from "../../Utils/Configs/apiConfig";

export type SubmissionStatus =
  | "Inbox"
  | "Archive"
  | "Deleted"
  | "Merged"
  | "Blocked duplicate";

export type SubmissionAction = "Show" | "Review" | "Restore";

export interface SubmissionHistoryItem {
  invoiceId: number;
  status: SubmissionStatus;
  action: SubmissionAction;
  submittedAt: string | null;
  submittedBy: string | null;
  submissionMethod: string | null;
  supplier: string | null;
  totalAmount: number | null;
  currency: string | null;
  documentUrl: string | null;
  showInvoiceId: number; // merged rows open the merged result
  duplicateFlagId: number | null; // set on Review rows
  restoreTarget: string | null; // "Inbox", "Archive" or a stage name
}

export interface SubmissionHistoryQuery {
  clientUserId: string;
  search?: string;
  status?: SubmissionStatus;
  page: number;
  limit: number;
  signal?: AbortSignal;
}

export const getSubmissionHistory = async ({
  signal,
  ...params
}: SubmissionHistoryQuery): Promise<{ items: SubmissionHistoryItem[]; totalCount: number }> => {
  const response = await ApiConfig.getInstance().get("/submission-history", {
    params,
    signal,
  });
  return response.data.data;
};

export const restoreSubmission = async (
  invoiceId: number,
  clientUserId: string
): Promise<{ restoredTo: string; item: SubmissionHistoryItem | null }> => {
  const response = await ApiConfig.getInstance().post(
    `/submission-history/${invoiceId}/restore`,
    null,
    { params: { clientUserId } }
  );
  return response.data.data;
};

const extensionOf = (url: string | null) => {
  const match = url?.split("?")[0].match(/\.[a-z0-9]+$/i);
  return match ? match[0] : "";
};

// Saves the stored file as {ItemId}{original extension}, e.g. 13082.pdf.
export const downloadSubmissionFile = async (
  item: SubmissionHistoryItem,
  clientUserId: string
) => {
  const response = await ApiConfig.getInstance().get(
    `/submission-history/${item.invoiceId}/file`,
    { params: { clientUserId }, responseType: "blob" }
  );

  const disposition: string = response.headers["content-disposition"] ?? "";
  const headerName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const fileName = headerName ?? `${item.invoiceId}${extensionOf(item.documentUrl)}`;

  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
